import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { type Comment, type GetNodeCommentsResponse } from '@/types/comments';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { updateNodeComment } from '@/services/comment.service';
import { useShareConfig } from '@/context/ShareConfigContext';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { QueryKey } from '@/lib/queryKeys';
import { useCapabilities } from '../useCapabilities';
import { useTranslations } from 'next-intl';

export type UpdateNodeCommentMutationParams = {
  commentId: string;
  body: string;
  nodeId: string;
  private?: boolean;
};

export const applyUpdatedCommentToNodeCommentsResponse = (
  currentCommentsResponse: InfiniteData<GetNodeCommentsResponse> | undefined,
  {
    commentId,
    updatedComment,
    private: updatedPrivate,
  }: {
    commentId: string;
    updatedComment: Comment;
    // Only applies to the root comment match below — replies are never private.
    private?: boolean | null;
  },
) => {
  if (!currentCommentsResponse) {
    return currentCommentsResponse;
  }

  let hasChanges = false;

  const nextPages = currentCommentsResponse.pages.map((page) => {
    let pageHasChanges = false;

    const nextComments = page.comments.map((nodeComment) => {
      if (nodeComment.commentId === commentId) {
        pageHasChanges = true;
        hasChanges = true;
        return {
          ...nodeComment,
          comment: updatedComment,
          ...(updatedPrivate !== undefined && { private: updatedPrivate ?? false }),
        };
      }

      let hasReplyChanges = false;

      const nextLatestReplies = nodeComment.latestReplies.map((reply) => {
        if (reply.id !== commentId) {
          return reply;
        }

        hasReplyChanges = true;
        return updatedComment;
      });

      if (!hasReplyChanges) {
        return nodeComment;
      }

      pageHasChanges = true;
      hasChanges = true;
      return {
        ...nodeComment,
        latestReplies: nextLatestReplies,
      };
    });

    if (!pageHasChanges) {
      return page;
    }

    return {
      ...page,
      comments: nextComments,
    };
  });

  if (!hasChanges) {
    return currentCommentsResponse;
  }

  return {
    ...currentCommentsResponse,
    pages: nextPages,
  };
};

export const useUpdateNodeCommentMutation = () => {
  const tCommon = useTranslations('common');
  const tErrors = useTranslations('errors');
  const tToasts = useTranslations('toasts');
  const queryClient = useQueryClient();
  const { canCommentPrivately } = useCapabilities();

  const { toast } = useToast();

  const shareConfig = useShareConfig();
  const updateNodeCommentWithAuth = useAuthenticatedQueryFn(updateNodeComment);

  const updateNodeCommentMutation = useMutation({
    mutationFn: ({ commentId, body, private: isPrivate }: UpdateNodeCommentMutationParams) =>
      updateNodeCommentWithAuth({
        commentId,
        body,
        // Never send private:true while the feature is flagged off, regardless of caller state.
        private: canCommentPrivately ? isPrivate : false,
      }),
    onSuccess: () => {},
    onError: async (error: any, { nodeId }) => {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description:
          error.status === 404
            ? tToasts('updateNodeCommentMutation.error')
            : (error.message ?? tErrors('toastDefaultDescription')),
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id],
      });
    },
  });

  return {
    updateNodeCommentMutation,
  };
};
